import React from 'react';
import { Box, Text, Flex, Loader } from '@opendoor/bricks/core';
import { useLateCheckout, ILateCheckoutTextData } from 'sellDirect/graphql/useLateCheckout';
import { UnderContractOffer } from 'sellDirect/OfferContext';
import { ClosingTasks } from 'sellDirect/helpers/closingTasks';
import { LocalDate } from '@js-joda/core';

/**
 * Late checkout costs.
 *
 * component-test-check-ignore
 */
export const LateCheckoutCostsWithoutContext: React.FC<ILateCheckoutTextData> = ({
  costPerDayDollarsText,
  fixedCostDollarsText,
  numberOfDays,
  totalCostDollarsText,
  freeDaysCreditText,
  totalCostDollarsAllDaysText,
  lateCheckoutConfirmed,
  freeDaysLateCheckout,
  isDeciding,
}) => (
  <Box data-testid="late-checkout-costs">
    {lateCheckoutConfirmed && !isDeciding ? (
      <Text>
        We'll deduct {freeDaysLateCheckout ? totalCostDollarsText : totalCostDollarsAllDaysText}{' '}
        from your net proceeds.
      </Text>
    ) : (
      <Box>
        <Text mb="4" size="s1" fontWeight="semibold">
          Cost breakdown
        </Text>

        <Flex justifyContent="space-between">
          <Text mb="4">
            {fixedCostDollarsText} {costPerDayDollarsText} a day for {numberOfDays} days
          </Text>
          <Text mb="4">{totalCostDollarsAllDaysText}</Text>
        </Flex>

        {!!freeDaysLateCheckout && (
          <>
            <Flex justifyContent="space-between">
              <Text mb="4">{Math.min(numberOfDays, freeDaysLateCheckout)} days free promo</Text>
              <Flex justifyContent="flex-end">
                <Text>-{freeDaysCreditText}</Text>
              </Flex>
            </Flex>
            <Flex justifyContent="space-between">
              <Text size="s1">Total cost:</Text>
              <Text size="s1">{totalCostDollarsText}</Text>
            </Flex>
          </>
        )}
      </Box>
    )}
  </Box>
);

interface ILateCheckoutCosts {
  offer: UnderContractOffer;
  lateCheckoutTask: ClosingTasks['lateCheckout'];
  lateCheckoutDateOverride?: LocalDate;
}

/** TODO: Add documentation */
export const LateCheckoutCosts: React.FC<ILateCheckoutCosts> = ({
  offer,
  lateCheckoutTask,
  lateCheckoutDateOverride,
}) => {
  const { loading, data } = useLateCheckout(offer, lateCheckoutTask, lateCheckoutDateOverride);
  if (loading) {
    return <Loader padding="less" />;
  }

  return <LateCheckoutCostsWithoutContext {...data} />;
};
